import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PaperPlaneTilt, Plus } from "@phosphor-icons/react";

// For category reports pass categoryName (+ optional projectId).
// For other reports pass a custom `title`, `endpoint` and `extraPayload`.
export function EmailReportDialog({
  open, onOpenChange, categoryName, projectId = null,
  title, endpoint = "/categories/line-items/email", extraPayload = null, onSent,
}) {
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [contacts, setContacts] = useState([]);

  const label = title || categoryName;

  useEffect(() => {
    if (open) {
      setEmailTo("");
      api.get("/team/report-contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
    }
  }, [open]);

  const addContact = (email) => {
    const current = emailTo.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
    if (current.map((e) => e.toLowerCase()).includes(email.toLowerCase())) return;
    setEmailTo(current.length ? `${emailTo.replace(/[,;\s]+$/, "")}, ${email}` : email);
  };

  const send = async () => {
    const recipients = emailTo.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    if (recipients.length === 0) { toast.error("Enter at least one valid email"); return; }
    setSending(true);
    try {
      const payload = extraPayload
        ? { recipients, ...extraPayload }
        : { name: categoryName, recipients, ...(projectId ? { project_id: projectId } : {}) };
      const { data: res } = await api.post(endpoint, payload);
      if (res.sent > 0) toast.success(res.message);
      else toast.message(res.message);
      onSent?.();
      onOpenChange(false);
    } catch { toast.error("Could not send report"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="email-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Email "{label}" report</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Sends the branded report as an attachment. Separate multiple emails with commas.
        </p>
        <Input
          data-testid="email-report-input"
          placeholder="client@example.com, lender@bank.com"
          value={emailTo}
          onChange={(e) => setEmailTo(e.target.value)}
          className="rounded-none"
        />
        {contacts.length > 0 && (
          <div data-testid="email-report-contacts">
            <p className="label-caps mb-2">Saved contacts — click to add</p>
            <div className="flex flex-wrap gap-2">
              {contacts.map((c) => (
                <button key={c.id} onClick={() => addContact(c.email)} data-testid={`email-contact-${c.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border hover:border-primary hover:text-primary transition-colors">
                  <Plus size={11} weight="bold" /> {c.name || c.email}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} data-testid="email-report-cancel">
            Cancel
          </Button>
          <Button className="rounded-none" onClick={send} disabled={sending} data-testid="email-report-send">
            <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
